import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import EntHero from '../../components/enterprise/EntHero';
import EntSkeleton from '../../components/enterprise/EntSkeleton';
import EntEmptyState from '../../components/enterprise/EntEmptyState';
import EntMeter from '../../components/enterprise/EntMeter';
import EntPerson from '../../components/enterprise/EntPerson';
import EntToolbar from '../../components/enterprise/EntToolbar';
import EntConfirmDialog from '../../components/enterprise/EntConfirmDialog';
import { roleLabel } from '../../utils/companyRoles';
import { formatDate } from '../../utils/enterpriseUi';

function apiMessage(err, fallback) {
  return err.response?.data?.message || err.response?.data?.error || fallback;
}

function isProMember(member) {
  return member?.seatTier === 'team_pro';
}

function planLabel(member) {
  // Short labels — the Seats page already frames these as Teams member access.
  return isProMember(member) ? 'Pro' : 'Basic';
}

function statusLabel(status) {
  if (!status) return '—';
  return String(status).replace(/_/g, ' ');
}

function nextScheduledTotals(capacity) {
  const scheduled = capacity?.scheduled;
  if (!scheduled) return null;
  const members =
    (capacity.members?.included || 0)
    + (Number(scheduled.additionalMembers) || 0)
    + (capacity.members?.promotional || 0);
  const proLicences =
    (capacity.proLicences?.included || 0)
    + (Number(scheduled.additionalProLicences) || 0)
    + (capacity.proLicences?.promotional || 0);
  return { members, proLicences, effectiveAt: scheduled.effectiveAt };
}

export default function EnterpriseSeatsPage() {
  const { enterpriseId } = useParams();
  const { showSuccess, showError } = useToast();
  const [capacity, setCapacity] = useState(null);
  const [capabilities, setCapabilities] = useState({});
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState('');
  const [offboardTarget, setOffboardTarget] = useState(null);
  const [buyMembers, setBuyMembers] = useState(5);
  const [buyPro, setBuyPro] = useState(1);
  const [releaseIds, setReleaseIds] = useState([]);
  const [buying, setBuying] = useState(false);
  const [buyMessage, setBuyMessage] = useState(null);

  const load = useCallback(async () => {
    const [capRes, membersRes] = await Promise.all([
      api.get('/api/enterprise/capabilities', { params: { enterpriseId } }),
      api.get('/api/enterprise/members', { params: { enterpriseId } }),
    ]);
    const nextCapacity = capRes.data.capacity || null;
    setCapacity(nextCapacity);
    setCapabilities(capRes.data.capabilities || {});
    setMembers(membersRes.data.members || []);
    if (nextCapacity?.members?.total != null) setBuyMembers(nextCapacity.members.total);
    if (nextCapacity?.proLicences?.total != null) setBuyPro(nextCapacity.proLicences.total);
    setReleaseIds([]);
  }, [enterpriseId]);

  useEffect(() => {
    if (!enterpriseId) return undefined;
    let cancelled = false;
    setLoading(true);
    load()
      .catch((err) => {
        if (!cancelled) showError(apiMessage(err, 'Could not load seats'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enterpriseId, load, showError]);

  const assignPro = async (userId) => {
    setBusyId(userId);
    try {
      await api.post('/api/enterprise/licences/assign', { userId }, { params: { enterpriseId } });
      showSuccess('Pro licence assigned.');
      await load();
    } catch (err) {
      showError(apiMessage(err, 'Could not assign Pro'));
    } finally {
      setBusyId(null);
    }
  };

  const removePro = async (userId) => {
    setBusyId(userId);
    try {
      await api.post('/api/enterprise/licences/remove', { userId }, { params: { enterpriseId } });
      showSuccess('Pro licence removed. They stay on Basic.');
      await load();
    } catch (err) {
      showError(apiMessage(err, 'Could not remove Pro'));
    } finally {
      setBusyId(null);
    }
  };

  const confirmOffboard = async () => {
    if (!offboardTarget) return;
    setBusyId(offboardTarget.userId);
    try {
      await api.post(
        `/api/enterprise/members/${offboardTarget.userId}/offboard`,
        {},
        { params: { enterpriseId } }
      );
      showSuccess('Person offboarded. Member capacity is free again.');
      setOffboardTarget(null);
      await load();
    } catch (err) {
      showError(apiMessage(err, 'Could not offboard'));
    } finally {
      setBusyId(null);
    }
  };

  const proAssigned = capacity?.proLicences?.assigned || 0;
  const proReduction = Math.max(0, proAssigned - Number(buyPro || 0));
  const proHolders = useMemo(() => members.filter(isProMember), [members]);

  const toggleRelease = (userId) => {
    setReleaseIds((current) => (
      current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId]
    ));
  };

  const submitCapacity = async (event) => {
    event.preventDefault();
    setBuyMessage(null);
    const membersTotal = Number(buyMembers);
    const proTotal = Number(buyPro);
    if (!Number.isInteger(membersTotal) || membersTotal < 0 || !Number.isInteger(proTotal) || proTotal < 0) {
      setBuyMessage('Member capacity and Pro licences must be whole numbers.');
      return;
    }
    if (proTotal > membersTotal) {
      setBuyMessage('Pro licences cannot exceed member capacity.');
      return;
    }
    if (proReduction > 0 && releaseIds.length !== proReduction) {
      setBuyMessage(`Select the ${proReduction} ${proReduction === 1 ? 'person' : 'people'} who will lose Pro.`);
      return;
    }
    setBuying(true);
    try {
      const res = await api.post('/api/billing/capacity', {
        enterpriseId,
        members: membersTotal,
        proLicences: proTotal,
        releaseProUserIds: releaseIds,
      });
      if (res.data?.url) {
        window.location.assign(res.data.url);
        return;
      }
      if (res.data?.scheduled) {
        showSuccess('Capacity change is scheduled for the next renewal.');
        await load();
        return;
      }
      showSuccess('Capacity updated.');
      await load();
    } catch (err) {
      const code = err.response?.data?.error;
      if (err.response?.status === 503 || code === 'checkout_disabled') {
        setBuyMessage(err.response?.data?.message || 'Checkout is not enabled.');
        return;
      }
      setBuyMessage(apiMessage(err, 'Could not update capacity'));
    } finally {
      setBuying(false);
    }
  };

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return members;
    return members.filter((m) => `${m.name || ''} ${m.email || ''} ${m.teamName || ''}`.toLowerCase().includes(needle));
  }, [members, query]);

  const scheduled = nextScheduledTotals(capacity);
  const canAssignPro = !!capabilities.canAssignPro;
  const canOffboard = !!capabilities.canOffboard;
  const canPurchase = !!capabilities.canPurchaseCapacity;

  const rowActions = (m) => (
    <>
      {canAssignPro ? (
        isProMember(m) ? (
          <button
            type="button"
            className="ent-btn-ghost ent-btn-sm"
            disabled={busyId === m.userId}
            onClick={() => removePro(m.userId)}
          >
            Remove Pro
          </button>
        ) : (
          <button
            type="button"
            className="ent-btn-secondary ent-btn-sm"
            disabled={busyId === m.userId}
            onClick={() => assignPro(m.userId)}
          >
            Assign Pro
          </button>
        )
      ) : null}
      {canOffboard ? (
        <button
          type="button"
          className="ent-btn-ghost ent-btn-sm"
          disabled={busyId === m.userId}
          onClick={() => setOffboardTarget(m)}
        >
          Offboard
        </button>
      ) : null}
    </>
  );

  const hero = (
    <EntHero
      title="Seats"
      lead="Member capacity is how many people can be in this workspace. Pro licences are an overlay for people who need Insights."
    />
  );

  if (loading) {
    return (
      <div className="ent-content">
        {hero}
        <EntSkeleton variant="cards" count={2} />
        <EntSkeleton variant="rows" count={5} />
      </div>
    );
  }

  return (
    <div className="ent-content">
      {hero}

      {capacity ? (
        <section className="ent-section">
          <h2 className="ent-section-title">Usage</h2>
          <div className="ent-panel" style={{ display: 'grid', gap: 18 }}>
            {capacity.overAllocated ? (
              <p className="ent-flash ent-flash-error" role="status">
                This workspace is over allocated. More people or Pro licences are in use than it currently includes.
                Remove Pro, offboard people, or purchase more capacity.
              </p>
            ) : null}

            <EntMeter
              label="Member capacity"
              used={capacity.members?.used || 0}
              total={capacity.members?.total || 0}
              hint={
                capacity.members?.remaining == null
                  ? undefined
                  : `${capacity.members.remaining} remaining${capacity.pendingInvites ? ` · ${capacity.pendingInvites} pending invites` : ''}`
              }
            />

            <EntMeter
              label="Pro licences"
              used={capacity.proLicences?.assigned || 0}
              total={capacity.proLicences?.total || 0}
              hint={
                capacity.proLicences?.remaining == null
                  ? undefined
                  : `${capacity.proLicences.remaining} remaining`
              }
            />

            {scheduled ? (
              <p className="ent-helper" style={{ margin: 0 }}>
                Next renewal
                {scheduled.effectiveAt ? ` (${formatDate(scheduled.effectiveAt)})` : ''}:
                {' '}{scheduled.members} member capacity, {scheduled.proLicences} Pro licences.
              </p>
            ) : null}
          </div>
        </section>
      ) : null}

      {canPurchase && capacity?.planFamily === 'teams' ? (
        <section className="ent-section">
          <h2 className="ent-section-title">Change capacity</h2>
          <form className="ent-panel" onSubmit={submitCapacity}>
            <div className="ent-form-grid">
              <div className="ent-form-group">
                <label htmlFor="buy-members">Member capacity</label>
                <input
                  id="buy-members"
                  className="ent-input"
                  type="number"
                  min={0}
                  step={1}
                  value={buyMembers}
                  onChange={(e) => setBuyMembers(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </div>
              <div className="ent-form-group">
                <label htmlFor="buy-pro">Pro licences</label>
                <input
                  id="buy-pro"
                  className="ent-input"
                  type="number"
                  min={0}
                  step={1}
                  value={buyPro}
                  onChange={(e) => {
                    setBuyPro(e.target.value === '' ? '' : Number(e.target.value));
                    setReleaseIds([]);
                  }}
                />
              </div>
            </div>
            {proReduction > 0 ? (
              <div className="ent-form-group">
                <p className="ent-helper" style={{ marginTop: 0 }}>
                  Choose {proReduction} {proReduction === 1 ? 'person' : 'people'} who will lose Pro.
                </p>
                <div className="ent-check-list">
                  {proHolders.map((m) => (
                    <label key={m.userId}>
                      <input
                        type="checkbox"
                        checked={releaseIds.includes(String(m.userId))}
                        onChange={() => toggleRelease(String(m.userId))}
                      />
                      <span>{m.name || m.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            ) : null}
            {buyMessage ? <p className="ent-form-error" role="status">{buyMessage}</p> : null}
            <button type="submit" className="ent-btn-primary" disabled={buying}>
              {buying ? 'Updating…' : 'Update capacity'}
            </button>
            <p className="ent-helper">
              Checkout is owner-only. If charging is not enabled, this will say so rather than pretending it succeeded.
            </p>
          </form>
        </section>
      ) : null}

      <section className="ent-section">
        <h2 className="ent-section-title">People</h2>
        <EntToolbar
          search={query}
          onSearch={setQuery}
          searchPlaceholder="Search people"
          searchLabel="Search people"
        />
        {members.length === 0 ? (
          <EntEmptyState title="No active members to assign" lead="Invite people first, then assign a Pro licence." />
        ) : visible.length === 0 ? (
          <EntEmptyState title="No people match" lead="Try a different search." />
        ) : (
          <>
            <div className="ent-table-wrap ent-hide-sm">
              <table className="ent-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Plan</th>
                    <th>Status</th>
                    <th>Team</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((m) => (
                    <tr key={m._id}>
                      <td><EntPerson name={m.name} email={m.email} /></td>
                      <td>{roleLabel(m)}</td>
                      <td>{planLabel(m)}</td>
                      <td>
                        <span className={`ent-chip ${m.status === 'active' ? 'ent-chip-active' : 'ent-chip-suspended'}`}>
                          {statusLabel(m.status)}
                        </span>
                      </td>
                      <td>{m.teamName || '—'}</td>
                      <td className="ent-actions-cell">{rowActions(m)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="ent-stack">
              {visible.map((m) => (
                <div key={m._id} className="ent-stack-card ent-stack-card-static">
                  <EntPerson name={m.name} email={m.email} meta={`${roleLabel(m)} · ${planLabel(m)}`} />
                  <div className="ent-stack-meta">
                    <span>{statusLabel(m.status)}</span>
                    <span>{m.teamName || 'No team'}</span>
                  </div>
                  <div className="ent-stack-actions">{rowActions(m)}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <EntConfirmDialog
        open={!!offboardTarget}
        title="Offboard this person?"
        message={
          offboardTarget
            ? `Remove ${offboardTarget.name || offboardTarget.email} from the company?`
            : ''
        }
        detail="Their captured contacts stay in the company CRM. Member capacity becomes free. They lose company access immediately."
        confirmLabel="Offboard"
        danger
        busy={busyId === offboardTarget?.userId}
        onConfirm={confirmOffboard}
        onClose={() => !busyId && setOffboardTarget(null)}
      />
    </div>
  );
}
